import { Liquid } from "liquidjs";

export type TranslationTokenKind = "liquid" | "html" | "url";

export interface TranslationToken {
  kind: TranslationTokenKind;
  value: string;
  start: number;
  end: number;
}

export type TranslationSafetyErrorCode =
  | "missing translation"
  | "source template syntax invalid"
  | "translation template syntax invalid"
  | "interpolation placeholders changed"
  | "liquid tokens changed"
  | "html tags changed"
  | "executable tokens changed";

export interface TranslationSafetyOptions {
  sourceLocale?: string;
  targetLocale?: string;
}

export type TranslationValidationResult =
  | { valid: true }
  | { valid: false; error: TranslationSafetyErrorCode };

export interface ProtectedTranslation {
  text: string;
  tokens: readonly TranslationToken[];
  restore: (translatedText: string) => string;
}

class TemplateSyntaxError extends Error {}

const LIQUID_DELIMITERS = [
  { open: "{{", close: "}}", kind: "liquid" as const },
  { open: "{%", close: "%}", kind: "liquid" as const },
];

const SIMPLE_INTERPOLATION_PATTERN =
  /^[A-Za-z_][A-Za-z0-9_.-]*(?:\s*,\s*[A-Za-z0-9_.-]+)?$/;

function findNextLiquidDelimiter(
  value: string,
  start: number,
): { index: number; open: string; close: string } | undefined {
  let next: { index: number; open: string; close: string } | undefined;
  for (const delimiter of LIQUID_DELIMITERS) {
    const index = value.indexOf(delimiter.open, start);
    if (index === -1 || (next && next.index <= index)) {
      continue;
    }
    next = { index, open: delimiter.open, close: delimiter.close };
  }
  return next;
}

function findLiquidTokenEnd(
  value: string,
  start: number,
  open: string,
  close: string,
): number | undefined {
  let quote: "'" | '"' | undefined;
  for (let index = start + open.length; index < value.length; index++) {
    const character = value[index];
    if (quote) {
      if (character === "\\") {
        index++;
      } else if (character === quote) {
        quote = undefined;
      }
      continue;
    }
    if (character === "'" || character === '"') {
      quote = character;
      continue;
    }
    if (value.startsWith(close, index)) {
      return index + close.length;
    }
  }
  return undefined;
}

function parseLiquid(value: string): void {
  try {
    const liquid = new Liquid({ templates: {} });
    // The Liquid parser only ships `elsif`; accept the `elseif` alias so
    // branch context can be compared instead of failing as invalid syntax.
    liquid.registerTag("elseif", {
      parse() {},
      render() {
        return "";
      },
    });
    liquid.parse(value);
  } catch {
    throw new TemplateSyntaxError("Invalid Liquid template");
  }
}

function scanLiquidTokens(value: string): TranslationToken[] {
  const tokens: TranslationToken[] = [];
  let cursor = 0;

  while (cursor < value.length) {
    const delimiter = findNextLiquidDelimiter(value, cursor);
    if (!delimiter) {
      break;
    }
    const end = findLiquidTokenEnd(
      value,
      delimiter.index,
      delimiter.open,
      delimiter.close,
    );
    if (end === undefined) {
      throw new TemplateSyntaxError("Unclosed Liquid token");
    }
    tokens.push({
      kind: "liquid",
      value: value.slice(delimiter.index, end),
      start: delimiter.index,
      end,
    });
    cursor = end;
  }

  return tokens;
}

function findHtmlTagEnd(value: string, start: number): number | undefined {
  let quote: "'" | '"' | undefined;
  for (let index = start + 1; index < value.length; index++) {
    const character = value[index];
    if (quote) {
      if (character === quote) {
        quote = undefined;
      }
      continue;
    }
    if (character === "'" || character === '"') {
      quote = character;
      continue;
    }
    if (character === ">") {
      return index + 1;
    }
  }
  return undefined;
}

function isHtmlTagStart(value: string, index: number): boolean {
  if (value[index] !== "<") {
    return false;
  }
  const next = value[index + 1];
  if (next === undefined) {
    return false;
  }
  if (next === "!" || next === "?") {
    return true;
  }
  if (next === "/") {
    return /^[A-Za-z]/.test(value[index + 2] ?? "");
  }
  return /^[A-Za-z]/.test(next);
}

function isInsideToken(
  index: number,
  tokens: readonly TranslationToken[],
): TranslationToken | undefined {
  return tokens.find((token) => index >= token.start && index < token.end);
}

function scanHtmlTokens(
  value: string,
  liquidTokens: readonly TranslationToken[],
): TranslationToken[] {
  const tokens: TranslationToken[] = [];
  let cursor = 0;

  while (cursor < value.length) {
    const liquidToken = isInsideToken(cursor, liquidTokens);
    if (liquidToken) {
      cursor = liquidToken.end;
      continue;
    }
    if (!value.startsWith("<!--", cursor) && !isHtmlTagStart(value, cursor)) {
      cursor++;
      continue;
    }

    let end: number | undefined;
    if (value.startsWith("<!--", cursor)) {
      const commentEnd = value.indexOf("-->", cursor + 4);
      end = commentEnd === -1 ? undefined : commentEnd + 3;
    } else {
      end = findHtmlTagEnd(value, cursor);
    }
    if (end === undefined) {
      throw new TemplateSyntaxError("Unclosed HTML tag");
    }
    tokens.push({
      kind: "html",
      value: value.slice(cursor, end),
      start: cursor,
      end,
    });
    cursor = end;
  }

  return tokens;
}

function scanUrlTokens(
  value: string,
  protectedTokens: readonly TranslationToken[],
): TranslationToken[] {
  const tokens: TranslationToken[] = [];
  const urlPattern = /\b(?:https?:\/\/|mailto:|tel:)[^\s<>"']+/gi;
  for (const match of value.matchAll(urlPattern)) {
    const start = match.index;
    const matchedValue = match[0];
    if (start === undefined || matchedValue === undefined) {
      continue;
    }
    const end = start + matchedValue.length;
    if (
      protectedTokens.some((token) => token.start < end && start < token.end)
    ) {
      continue;
    }
    tokens.push({ kind: "url", value: matchedValue, start, end });
  }
  return tokens;
}

const VOID_HTML_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

function validateHtmlNesting(tokens: readonly TranslationToken[]): void {
  const openTags: string[] = [];
  for (const token of tokens) {
    const trimmed = token.value.trim();
    if (
      trimmed.startsWith("<!--") ||
      trimmed.startsWith("<!") ||
      trimmed.startsWith("<?")
    ) {
      continue;
    }
    // "<custom domain>" is placeholder text, not a tag. Real tags put
    // attributes after "=".
    if (/^<[^>]*\s[^=>]*>$/.test(trimmed) && !trimmed.includes("=")) {
      continue;
    }
    const closingMatch = trimmed.match(/^<\s*\/\s*([A-Za-z][A-Za-z0-9:_-]*)/);
    if (closingMatch) {
      const expected = openTags.pop();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (expected !== closingMatch[1]!.toLowerCase()) {
        throw new TemplateSyntaxError("Mismatched HTML tag");
      }
      continue;
    }
    const openingMatch = trimmed.match(/^<\s*([A-Za-z][A-Za-z0-9:_-]*)/);
    if (!openingMatch) {
      continue;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const name = openingMatch[1]!.toLowerCase();
    if (!trimmed.endsWith("/>") && !VOID_HTML_TAGS.has(name)) {
      openTags.push(name);
    }
  }
  if (openTags.length > 0) {
    throw new TemplateSyntaxError("Unclosed HTML tag");
  }
}

/** Parse a translation template and return its executable token spans. */
export function extractTranslationTokens(
  value: string,
): readonly TranslationToken[] {
  parseLiquid(value);
  const liquidTokens = scanLiquidTokens(value);
  const htmlTokens = scanHtmlTokens(value, liquidTokens);
  const filteredLiquidTokens = liquidTokens.filter(
    (liquidToken) =>
      !htmlTokens.some(
        (htmlToken) =>
          htmlToken.start < liquidToken.end &&
          liquidToken.start < htmlToken.end,
      ),
  );
  validateHtmlNesting(htmlTokens);
  const executableTokens = [...filteredLiquidTokens, ...htmlTokens];
  return [...executableTokens, ...scanUrlTokens(value, executableTokens)].sort(
    (left, right) => left.start - right.start,
  );
}

function tokenSignature(token: TranslationToken): string {
  return `${token.kind}:${token.value}`;
}

function orderedTokenSignatures(
  tokens: readonly TranslationToken[],
  predicate?: (token: TranslationToken) => boolean,
): string[] {
  return tokens.filter(predicate ?? (() => true)).map(tokenSignature);
}

function sameSequence(
  left: readonly TranslationToken[],
  right: readonly TranslationToken[],
  predicate?: (token: TranslationToken) => boolean,
): boolean {
  const leftSignatures = orderedTokenSignatures(left, predicate);
  const rightSignatures = orderedTokenSignatures(right, predicate);
  return (
    leftSignatures.length === rightSignatures.length &&
    leftSignatures.every(
      (signature, index) => signature === rightSignatures[index],
    )
  );
}

function sameStringMultiset(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function isExecutableLiquidControlToken(token: TranslationToken): boolean {
  return token.kind === "liquid" && !isSimpleInterpolation(token);
}

function isSimpleInterpolation(token: TranslationToken): boolean {
  if (token.kind !== "liquid" || !token.value.startsWith("{{")) {
    return false;
  }
  const body = token.value.slice(2, -2).replace(/^-|-$/g, "").trim();
  return SIMPLE_INTERPOLATION_PATTERN.test(body);
}

const LIQUID_BLOCK_OPEN: Record<string, string> = {
  if: "endif",
  unless: "endunless",
  for: "endfor",
  case: "endcase",
};

const LIQUID_BLOCK_CLOSE = new Set(Object.values(LIQUID_BLOCK_OPEN));
const LIQUID_BLOCK_BRANCH = new Set(["else", "elsif", "elseif", "when"]);

function liquidCommand(value: string): string | undefined {
  if (!value.startsWith("{%")) {
    return undefined;
  }
  const body = value.slice(2, -2).replace(/^-|-$/g, "").trim();
  return /^([A-Za-z]+)/.exec(body)?.[1]?.toLowerCase();
}

type HtmlTagInfo =
  | { kind: "ignore" }
  | { kind: "open"; name: string }
  | { kind: "close"; name: string }
  | { kind: "void"; name: string };

function parseHtmlTag(token: TranslationToken): HtmlTagInfo | undefined {
  if (token.kind !== "html") {
    return undefined;
  }
  const trimmed = token.value.trim();
  if (
    trimmed.startsWith("<!--") ||
    trimmed.startsWith("<!") ||
    trimmed.startsWith("<?")
  ) {
    return { kind: "ignore" };
  }
  const closingMatch = trimmed.match(/^<\s*\/\s*([A-Za-z][A-Za-z0-9:_-]*)/);
  if (closingMatch?.[1]) {
    return { kind: "close", name: closingMatch[1].toLowerCase() };
  }
  const openingMatch = trimmed.match(/^<\s*([A-Za-z][A-Za-z0-9:_-]*)/);
  if (!openingMatch?.[1]) {
    return undefined;
  }
  const name = openingMatch[1].toLowerCase();
  if (trimmed.endsWith("/>") || VOID_HTML_TAGS.has(name)) {
    return { kind: "void", name };
  }
  return { kind: "open", name };
}

type InterpolationContextFrame =
  | { kind: "html"; name: string; index: number; children: Map<string, number> }
  | { kind: "liquid"; open: string; branch: string };

function popContextFrame(
  stack: InterpolationContextFrame[],
  match: (frame: InterpolationContextFrame) => boolean,
): void {
  for (let index = stack.length - 1; index >= 0; index--) {
    const frame = stack[index];
    if (frame && match(frame)) {
      stack.splice(index, 1);
      return;
    }
  }
}

function contextFrameSignature(frame: InterpolationContextFrame): string {
  if (frame.kind === "html") {
    return `html:${frame.name}:${frame.index}`;
  }
  return `liquid:${frame.open}\u0002${frame.branch}`;
}

function htmlChildrenOf(
  stack: InterpolationContextFrame[],
  root: Map<string, number>,
): Map<string, number> {
  for (let index = stack.length - 1; index >= 0; index--) {
    const frame = stack[index];
    if (frame?.kind === "html") {
      return frame.children;
    }
  }
  return root;
}

/** Simple `{{ identifier }}` tokens keyed by value and enclosing structure. */
function interpolationContextKeys(
  tokens: readonly TranslationToken[],
): string[] {
  const stack: InterpolationContextFrame[] = [];
  const htmlRootChildren = new Map<string, number>();
  const keys: string[] = [];

  for (const token of tokens) {
    if (isSimpleInterpolation(token)) {
      keys.push(
        `${token.value}\u0000${stack.map(contextFrameSignature).join("\u0001")}`,
      );
      continue;
    }

    const htmlTag = parseHtmlTag(token);
    if (htmlTag?.kind === "open") {
      const siblings = htmlChildrenOf(stack, htmlRootChildren);
      const index = siblings.get(htmlTag.name) ?? 0;
      siblings.set(htmlTag.name, index + 1);
      stack.push({
        kind: "html",
        name: htmlTag.name,
        index,
        children: new Map(),
      });
      continue;
    }
    if (htmlTag?.kind === "close") {
      popContextFrame(
        stack,
        (frame) => frame.kind === "html" && frame.name === htmlTag.name,
      );
      continue;
    }
    if (htmlTag) {
      continue;
    }

    if (token.kind !== "liquid") {
      continue;
    }
    const command = liquidCommand(token.value);
    if (!command) {
      continue;
    }
    if (command in LIQUID_BLOCK_OPEN) {
      stack.push({ kind: "liquid", open: token.value, branch: "" });
      continue;
    }
    if (LIQUID_BLOCK_BRANCH.has(command)) {
      for (let index = stack.length - 1; index >= 0; index--) {
        const frame = stack[index];
        if (frame?.kind === "liquid") {
          frame.branch = token.value;
          break;
        }
      }
      continue;
    }
    if (LIQUID_BLOCK_CLOSE.has(command)) {
      popContextFrame(stack, (frame) => {
        if (frame.kind !== "liquid") {
          return false;
        }
        const openCommand = liquidCommand(frame.open);
        return (
          openCommand !== undefined &&
          LIQUID_BLOCK_OPEN[openCommand] === command
        );
      });
    }
  }

  return keys.sort();
}

function replaceTokens(
  value: string,
  tokens: readonly TranslationToken[],
): ProtectedTranslation {
  const markers: string[] = [];
  for (const [index] of tokens.entries()) {
    let marker = `⟪REDO_TOKEN_${index}⟫`;
    let collision = 0;
    while (value.includes(marker) || markers.includes(marker)) {
      collision++;
      marker = `⟪REDO_TOKEN_${index}_${collision}⟫`;
    }
    markers.push(marker);
  }
  let text = "";
  let cursor = 0;
  for (const [index, token] of tokens.entries()) {
    text += value.slice(cursor, token.start);
    text += markers[index];
    cursor = token.end;
  }
  text += value.slice(cursor);

  return {
    text,
    tokens,
    restore: (translatedText) => {
      let restored = translatedText;
      for (const [index, marker] of markers.entries()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        restored = restored.split(marker).join(tokens[index]!.value);
      }
      return restored;
    },
  };
}

/** Replace executable tokens with opaque markers before model translation. */
export function protectTranslationTokens(value: string): ProtectedTranslation {
  return replaceTokens(value, extractTranslationTokens(value));
}

export function getInterpolationPlaceholders(value: string): string[] {
  return extractTranslationTokens(value)
    .filter(isSimpleInterpolation)
    .map((token) => token.value)
    .sort();
}

function interpolationValues(tokens: readonly TranslationToken[]): string[] {
  return tokens
    .filter(isSimpleInterpolation)
    .map((token) => token.value)
    .sort();
}

function getTokenChangeError(
  sourceTokens: readonly TranslationToken[],
  translationTokens: readonly TranslationToken[],
): TranslationSafetyErrorCode | undefined {
  if (
    !sameStringMultiset(
      interpolationValues(sourceTokens),
      interpolationValues(translationTokens),
    )
  ) {
    return "interpolation placeholders changed";
  }

  if (
    !sameSequence(
      sourceTokens,
      translationTokens,
      isExecutableLiquidControlToken,
    )
  ) {
    return "liquid tokens changed";
  }

  if (
    !sameSequence(
      sourceTokens,
      translationTokens,
      (token) => token.kind === "html",
    )
  ) {
    return "html tags changed";
  }

  if (
    !sameSequence(
      sourceTokens,
      translationTokens,
      (token) => token.kind === "url",
    )
  ) {
    return "executable tokens changed";
  }

  if (
    !sameStringMultiset(
      interpolationContextKeys(sourceTokens),
      interpolationContextKeys(translationTokens),
    )
  ) {
    return "interpolation placeholders changed";
  }

  return undefined;
}

/**
 * Return a stable validation error, or undefined when a model translation is
 * safe to persist.
 */
export function getTranslationSafetyError(
  source: string,
  translation: string | undefined,
  _options?: TranslationSafetyOptions | string,
): TranslationSafetyErrorCode | undefined {
  if (!translation || translation.trim() === "") {
    return "missing translation";
  }

  let sourceTokens: readonly TranslationToken[];
  try {
    sourceTokens = extractTranslationTokens(source);
  } catch {
    return "source template syntax invalid";
  }

  let translationTokens: readonly TranslationToken[];
  try {
    translationTokens = extractTranslationTokens(translation);
  } catch {
    return "translation template syntax invalid";
  }

  return getTokenChangeError(sourceTokens, translationTokens);
}

export function validateTranslation(
  source: string,
  translation: string | undefined,
  options?: TranslationSafetyOptions | string,
): TranslationValidationResult {
  const error = getTranslationSafetyError(source, translation, options);
  return error ? { valid: false, error } : { valid: true };
}
